import { useEffect, useMemo } from 'react'
import { FormInstance } from 'antd'
import { useSelector } from 'react-redux'
import { useWatch } from 'antd/es/form/Form'
import { parseDate } from '../../../config/dayjsConfig'
import { currentUserSortedCompaniesSelector, currentUserSelector } from '../../../redux/session/currentUser/selectors'
import {
  DailyDelivery,
  DeliverySubscription,
  ManualDelivery,
  MonthlyDelivery,
  PeriodLockDelivery,
  WeeklyDelivery
} from '../../../types/deliverySubscription/DeliverySubscription'
import { transformFieldData } from '../../../utils/form'
import { useCompanyUsers } from '../../users/queries/useCompanyUsers'
import { DeliverySubscriptionFormData } from '../types'

export const useDeliverySubscriptionForm = (
  form: FormInstance<DeliverySubscriptionFormData>,
  deliverySubscription: DeliverySubscription | undefined
) => {
  const selectableCompanies = useSelector(currentUserSortedCompaniesSelector)
  const currentUser = useSelector(currentUserSelector)
  const companyId = useWatch('companyId', form)
  const { data: users = [] } = useCompanyUsers(companyId)

  useEffect(() => {
    form.resetFields()
    if (deliverySubscription) {
      if (((deliverySubscription.initiator as MonthlyDelivery).monthly as { day: number })?.day) {
        form.setFieldValue(['initiator', 'type'], 'monthly')
      }
      if (((deliverySubscription.initiator as WeeklyDelivery).weekly as { dow: number })?.dow) {
        form.setFieldValue(['initiator', 'type'], 'weekly')
      }
      if ((deliverySubscription.initiator as DailyDelivery).daily) {
        form.setFieldValue(['initiator', 'type'], 'daily')
      }
      const { periodLock } = deliverySubscription.initiator as PeriodLockDelivery
      if (periodLock === true || (periodLock as unknown) === 'true') {
        form.setFieldValue(['initiator', 'type'], 'periodLock')
      }
      if ((deliverySubscription.initiator as ManualDelivery).manual) {
        form.setFieldValue(['initiator', 'type'], 'manual')
      }

      const cfg = {
        endDate: (value: any) => value && parseDate(value)?.endOf('month'),
        recipients: (value: any) => value || undefined
      }
      form.setFieldsValue(transformFieldData(deliverySubscription, cfg))
      form.setFieldValue(['params', 'printVersion'], deliverySubscription.params?.printVersion ?? 'v1')
    }
  }, [deliverySubscription])

  const selectableUsers = useMemo(() => {
    const result = [...users]
    const subUser = deliverySubscription?.user
    if (subUser && !result.find(u => u.id === subUser.id)) result.push(subUser)
    if (!result.find(u => u.id === currentUser.id)) result.push(currentUser)
    return result
  }, [users, deliverySubscription?.user, currentUser])

  return {
    selectableCompanies,
    selectableUsers,
    companyId,
    selectedCompany: selectableCompanies.find(c => c.id === companyId)
  }
}
